import React from 'react';
import PropTypes from 'prop-types';
import { Button, SparkIcon, SparkModalPortal, Spinner, Text } from '@spark';
import '@/features/datasets/components/DataCollection/GenerateDataWithAI/generateWithAI.css';

/**
 * 820px Spark modal shell shared by every "Generate data with AI" flow and
 * the Retrieval Agent: header (title + subtitle + close), scrollable body,
 * and a footer (Figma 9944:23194) with optional leading content (the cost
 * estimate) on the left and the actions on the right — "Back" next to the
 * primary button on every step after the first; the first step has no
 * secondary button, the header X closes the modal.
 */
const GenAiModalShell = ({
    open,
    onClose,
    title,
    subtitle,
    children,
    primaryLabel,
    onPrimary,
    primaryDisabled = false,
    primaryDisableWhenReadOnly = false,
    isSubmitting = false,
    showBack = false,
    onBack,
    readOnly = false,
    footerMeta,
    footerError = '',
    /** Leading footer content after Back (e.g. the pre-run cost estimate); hidden in read-only. */
    footerStart = null,
}) => (
    <SparkModalPortal
        open={open}
        onClose={onClose}
        destroyOnClose
        size="slot"
        // Above the Spark modal portal default (1100), matching the other
        // dataset modals that replaced antd zIndex 2000 dialogs.
        style={{ zIndex: 2000 }}
    >
        <div className="gdai-modal">
            <div className="gdai-modal__header">
                <div>
                    <div className="gdai-modal__title fs-unmask">{title}</div>
                    {subtitle ? <div className="gdai-modal__subtitle fs-unmask">{subtitle}</div> : null}
                </div>
                <button type="button" className="gdai-modal__close" aria-label="Close" onClick={onClose}>
                    <SparkIcon slug="close" size="medium" />
                </button>
            </div>
            <div className="gdai-modal__body">{children}</div>
            <div
                className={`gdai-modal__footer${
                    (readOnly && footerMeta) || (!readOnly && footerStart) ? ' gdai-modal__footer--with-start' : ''
                }`}
            >
                {!readOnly && footerStart ? (
                    <div className="gdai-modal__footer-start">{footerStart}</div>
                ) : null}
                {readOnly && footerMeta ? footerMeta : null}
                <div className="gdai-modal__footer-actions">
                    {!readOnly && footerError ? (
                        <Text
                            as="p"
                            variant="detail-regular"
                            color="inherit"
                            className="gdai-modal__footer-error fs-unmask"
                            role="alert"
                        >
                            {footerError}
                        </Text>
                    ) : null}
                    <div className="gdai-modal__footer-buttons">
                        {readOnly ? (
                            <Button variant="primary" size="medium" onClick={onClose}>
                                Close
                            </Button>
                        ) : (
                            <>
                                {showBack ? (
                                    <Button
                                        variant="tertiary"
                                        size="medium"
                                        className="gdai-modal__back fs-unmask"
                                        onClick={onBack}
                                        disabled={isSubmitting}
                                    >
                                        Back
                                    </Button>
                                ) : null}
                                <Button
                                    variant="primary"
                                    size="medium"
                                    onClick={onPrimary}
                                    disabled={primaryDisabled || isSubmitting}
                                    disableWhenReadOnly={primaryDisableWhenReadOnly}
                                    startIcon={isSubmitting ? <Spinner size="small" /> : undefined}
                                >
                                    {primaryLabel}
                                </Button>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </div>
    </SparkModalPortal>
);

GenAiModalShell.propTypes = {
    open: PropTypes.bool.isRequired,
    onClose: PropTypes.func.isRequired,
    title: PropTypes.string.isRequired,
    subtitle: PropTypes.string,
    children: PropTypes.node,
    primaryLabel: PropTypes.string,
    onPrimary: PropTypes.func,
    primaryDisabled: PropTypes.bool,
    primaryDisableWhenReadOnly: PropTypes.bool,
    isSubmitting: PropTypes.bool,
    showBack: PropTypes.bool,
    onBack: PropTypes.func,
    readOnly: PropTypes.bool,
    footerMeta: PropTypes.node,
    footerError: PropTypes.string,
    footerStart: PropTypes.node,
};

export default GenAiModalShell;
